import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import './Backtest.css';
import './Backtest2.css';
import './MemberView.css';
import './NewMemberView.css';
import AiCommentary from './AiCommentary';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Locked production symbols — not user-configurable on the member view.
const LOCKED = { tech: 'QQQ', momentum: 'MTUM', def1: 'GLD', def2: 'IEF', cash: 'BIL' };

// Map live tickers to friendly category labels (no math).
const FRIENDLY = {
  QQQ: 'Tech Growth',
  MTUM: 'Momentum Leaders',
  GLD: 'Defensive (Gold)',
  IEF: 'Defensive (Treasury Bonds)',
  BIL: 'Defensive (Cash Reserve)',
};

const PASSCODE = 'gratitude';
const SESSION_KEY = 'nmv_unlocked_v1';

const NewMemberView = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('3y');
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [sortDesc, setSortDesc] = useState(true);

  // Passcode gate
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1',
  );
  const [passcodeInput, setPasscodeInput] = useState('');
  const [gateError, setGateError] = useState('');

  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    const submitted = (passcodeInput || '').trim().toLowerCase();
    if (submitted === PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, '1');
      setUnlocked(true);
      setGateError('');
    } else {
      setGateError('Incorrect passcode. Please try again.');
    }
  };

  const periodOptions = {
    '1y': '1 Year',
    '3y': '3 Years',
    '5y': '5 Years',
    '10y': '10 Years',
    inception: 'Since Inception',
    extended: 'Extended ~19Y',
  };

  // Strip proprietary math from trade-reason strings:
  //   1. Drop everything in parentheses
  //   2. Drop numeric score comparisons on entry rows
  const sanitizeReason = (reason) => {
    if (!reason) return reason;
    let s = String(reason);
    s = s.replace(/\s*\([^)]*\)/g, '');
    s = s.replace(
      /:\s*([A-Z]{1,6})\s+-?\d+\.\d+\s*[><=]+\s*[A-Z]{1,6}\s+-?\d+\.\d+/g,
      ': $1',
    );
    return s.trim();
  };

  const positionTagClass = (sym) => {
    if (!sym) return '';
    const s = sym.toUpperCase();
    if (s === LOCKED.tech) return 'bt2-tag-tech';
    if (s === LOCKED.momentum) return 'bt2-tag-momentum';
    if ([LOCKED.def1, LOCKED.def2, LOCKED.cash].includes(s)) return 'bt2-tag-defensive';
    return '';
  };

  const runBacktest = async (force = false, p = period) => {
    setLoading(true);
    setError(null);
    try {
      // 'inception' → MTUM native max (~12.5y). 'extended' → XMMO-spliced ~19y.
      let apiPeriod = p;
      let extendedFlag = false;
      if (p === 'inception') {
        apiPeriod = 'max';
      } else if (p === 'extended') {
        apiPeriod = 'max';
        extendedFlag = true;
      }
      const asOf = new URLSearchParams(window.location.search).get('as_of');
      const asOfQS = asOf ? `&as_of=${asOf}` : '';
      const url = `${API_URL}/api/gt2/run?force=${force}&period=${apiPeriod}&extended=${extendedFlag}${asOfQS}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setResults(data);
      } else {
        setError('Unable to load performance data at the moment.');
      }
    } catch (err) {
      setError('Connection error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) runBacktest();
  }, [unlocked]); // eslint-disable-line

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    runBacktest(false, newPeriod);
  };

  const formatCurrency = (val) => `$${val?.toLocaleString() || 0}`;
  const sig = results?.current_signal;
  const isRotate = sig?.action === 'ROTATE';
  const displaySymbol = isRotate ? (sig?.next_target || LOCKED.tech) : (sig?.current_holding || LOCKED.tech);
  const actionVerb = isRotate ? 'Rotating to' : 'Holding';

  const MetricCard = ({ label, value, subValue, color }) => (
    <div className="bt-metric-card">
      <span className="bt-metric-label">{label}</span>
      <span className="bt-metric-value" style={color ? { color } : {}}>
        {value}
      </span>
      {subValue && <span className="bt-metric-sub">{subValue}</span>}
    </div>
  );

  // 🔒 Passcode gate — Grateful Trust frontdoor
  if (!unlocked) {
    return (
      <div className="mv-gate-page" data-testid="newmemberview-gate">
        <div className="mv-gate-card">
          <div className="mv-gate-brand">
            <img src="/logo.png" alt="Pro Stock Money Bowl" className="mv-gate-logo" />
            <div className="mv-gate-titles">
              <div className="mv-gate-eyebrow">GRATEFUL TRUST</div>
              <h1 className="mv-gate-title">The Serene System</h1>
              <div className="mv-gate-subtitle">Historical Performance · Member Access</div>
            </div>
          </div>

          <form className="mv-gate-form" onSubmit={handlePasscodeSubmit}>
            <label className="mv-gate-label" htmlFor="nmv-gate-input">
              Enter access passcode
            </label>
            <input
              id="nmv-gate-input"
              type="password"
              className="mv-gate-input"
              value={passcodeInput}
              onChange={(e) => { setPasscodeInput(e.target.value); setGateError(''); }}
              placeholder="Passcode"
              autoFocus
              autoComplete="off"
              data-testid="newmemberview-passcode-input"
            />
            {gateError && (
              <div className="mv-gate-error" data-testid="newmemberview-gate-error">
                {gateError}
              </div>
            )}
            <button
              type="submit"
              className="mv-gate-button"
              data-testid="newmemberview-passcode-submit"
            >
              Unlock
            </button>
          </form>

          <div className="mv-gate-footer">
            Access provided to invited members of the <strong>Grateful Trust Community</strong>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bt-container mv-page" data-testid="newmemberview-page">
      <div className="bt-header mv-header-stack">
        <h1 className="bt2-title mv-title">The Serene System</h1>
        <div className="mv-subtitle">Historical Performance</div>
        <p className="mv-description">
          A disciplined, rules-based rotation strategy that systematically allocates 100% of
          capital across a focused set of carefully selected assets — growth, momentum, and an
          adaptive defensive sleeve that shifts among gold, U.S. Treasury bonds, and cash
          reserves depending on the prevailing market environment. Backtested across two
          decades and multiple market cycles to deliver strong returns with dramatically lower
          drawdowns than a passive S&amp;P 500 buy-and-hold.
        </p>
      </div>

      {/* Today's Position — sanitized: no math, no explainer text */}
      <div className="mv-position-wrap" data-testid="newmemberview-today-position">
        <div className={`mv-position-card ${isRotate ? 'mv-position-card--rotate' : ''}`}>
          <div className="mv-position-header">
            <span className="mv-position-label">Today&apos;s Position</span>
            {sig?.as_of_date && (
              <span className="mv-position-date">as of {sig.as_of_date}</span>
            )}
          </div>
          <div className="mv-position-action">
            {actionVerb} <strong>{displaySymbol}</strong>
          </div>
          <div className="mv-position-sub">{FRIENDLY[displaySymbol] || ''}</div>
        </div>
      </div>

      <div className="bt-actions mv-actions">
        <div
          className="bt-period-selector"
          data-testid="newmemberview-period-selector"
        >
          <span className="bt-selector-label">Period:</span>
          {Object.entries(periodOptions).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handlePeriodChange(key)}
              className={`bt-period-btn ${period === key ? 'active' : ''}`}
              disabled={loading}
              data-testid={`newmemberview-period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="bt-loading" data-testid="newmemberview-loading">
          <div className="bt-spinner"></div>
          <p>Loading performance data…</p>
        </div>
      )}

      {error && (
        <div className="bt-error" data-testid="newmemberview-error">
          {error}
        </div>
      )}

      {results && !loading && (
        <>
          <div className="bt-period" data-testid="newmemberview-period">
            Performance Window:{' '}
            <strong>{results.backtest_period.start}</strong> to{' '}
            <strong>{results.backtest_period.end}</strong>
            {' '}({results.backtest_period.years} years)
          </div>

          <div className="bt-metrics-section">
            <h2>Strategy Performance</h2>
            <div
              className="bt-metrics-row"
              data-testid="newmemberview-strategy-metrics"
            >
              <MetricCard
                label="Ending Value"
                value={formatCurrency(results.strategy_metrics.ending_value)}
                subValue={`from ${formatCurrency(results.strategy_metrics.starting_value)}`}
              />
              <MetricCard
                label="Total Return"
                value={`${results.strategy_metrics.total_return_pct > 0 ? '+' : ''}${results.strategy_metrics.total_return_pct}%`}
                color={
                  results.strategy_metrics.total_return_pct >= 0
                    ? '#22c55e'
                    : '#ef4444'
                }
              />
              <MetricCard
                label="CAGR"
                value={`${results.strategy_metrics.cagr}%`}
                color={
                  results.strategy_metrics.cagr >= 0 ? '#22c55e' : '#ef4444'
                }
              />
              <MetricCard
                label="Max Drawdown"
                value={`-${results.strategy_metrics.max_drawdown}%`}
                color="#f59e0b"
              />
              <MetricCard
                label="Sharpe Ratio"
                value={results.strategy_metrics.sharpe_ratio}
                color={
                  results.strategy_metrics.sharpe_ratio >= 1
                    ? '#22c55e'
                    : '#f59e0b'
                }
              />
              <MetricCard
                label="Total Trades"
                value={results.strategy_metrics.total_trades}
                subValue={
                  results.backtest_period?.years
                    ? `${(results.strategy_metrics.total_trades / results.backtest_period.years).toFixed(1)} / year`
                    : null
                }
                color="#06b6d4"
              />
            </div>
          </div>

          <div className="bt-metrics-section benchmark">
            <h2>S&amp;P 500 Buy-and-Hold Benchmark</h2>
            <div
              className="bt-metrics-row"
              data-testid="newmemberview-spy-metrics"
            >
              <MetricCard
                label="Ending Value"
                value={formatCurrency(results.spy_benchmark.ending_value)}
                subValue="from $100,000"
              />
              <MetricCard
                label="Total Return"
                value={`${results.spy_benchmark.total_return_pct > 0 ? '+' : ''}${results.spy_benchmark.total_return_pct}%`}
                color={
                  results.spy_benchmark.total_return_pct >= 0
                    ? '#22c55e'
                    : '#ef4444'
                }
              />
              <MetricCard label="CAGR" value={`${results.spy_benchmark.cagr}%`} />
              <MetricCard
                label="Max Drawdown"
                value={`-${results.spy_benchmark.max_drawdown}%`}
                color="#f59e0b"
              />
            </div>
          </div>

          {(() => {
            const diff =
              results.strategy_metrics.total_return_pct -
              results.spy_benchmark.total_return_pct;
            const outperformed = diff > 0;
            const ddDiff =
              results.spy_benchmark.max_drawdown -
              results.strategy_metrics.max_drawdown;
            const ddBetter = ddDiff > 0;
            return (
              <div
                className={`bt-outperformance ${outperformed ? 'positive' : 'negative'}`}
                data-testid="newmemberview-outperformance"
              >
                Strategy {outperformed ? 'outperformed' : 'underperformed'} the
                S&amp;P 500 by{' '}
                <strong>
                  {Math.abs(diff).toFixed(1)} percentage points
                </strong>
                {' '}
                {ddBetter ? 'with' : 'but with'}{' '}
                <strong>{Math.abs(ddDiff).toFixed(1)}%</strong>
                {' '}
                {ddBetter ? 'less' : 'deeper'} drawdown
              </div>
            );
          })()}

          {/* Drawdown in Context — behavioral framing for the headline max drawdown */}
          {results.drawdown_context && (
            <div className="nmv-dd-context" data-testid="newmemberview-dd-context">
              <h2>
                {results.drawdown_context.dd_in_progress
                  ? 'Current Pullback (in progress)'
                  : 'Drawdown in Context'}
              </h2>
              <p className="nmv-dd-intro">
                {results.drawdown_context.dd_in_progress ? (
                  <>
                    The headline drawdown above is <strong>still in progress</strong> — it
                    has not yet recovered to a new all-time high. All numbers below are
                    <em> as of today</em> and will update once the strategy recovers.
                  </>
                ) : (
                  <>
                    The headline drawdown above is the single worst episode of the entire period.
                    Here&apos;s what a <strong>typical</strong> year - and the recovery from that
                    worst-case event - actually looked like.
                  </>
                )}
              </p>
              <div className="bt-metrics-row nmv-dd-row">
                <MetricCard
                  label="Typical Year's Deepest Dip"
                  value={
                    results.drawdown_context.typical_year_dip != null
                      ? `-${results.drawdown_context.typical_year_dip}%`
                      : '-'
                  }
                  subValue="average intra-year drawdown"
                  color="#f59e0b"
                />
                <MetricCard
                  label={
                    results.drawdown_context.dd_in_progress
                      ? 'S&P 500 Same Window'
                      : 'S&P 500 in Same Crash'
                  }
                  value={
                    results.drawdown_context.spy_same_crash != null
                      ? `-${results.drawdown_context.spy_same_crash}%`
                      : '-'
                  }
                  subValue={
                    results.drawdown_context.dd_in_progress
                      ? 'buy-and-hold, same window (as of today)'
                      : 'buy-and-hold, same window'
                  }
                  color={results.drawdown_context.dd_in_progress ? '#94a3b8' : '#ef4444'}
                />
                <MetricCard
                  label="Time to Full Recovery"
                  value={
                    results.drawdown_context.recovery_months != null
                      ? `${results.drawdown_context.recovery_months} months`
                      : 'in progress'
                  }
                  subValue="peak → new all-time high"
                  color="#22c55e"
                />
                <MetricCard
                  label="% of Time Near All-Time High"
                  value={`${results.drawdown_context.pct_near_high}%`}
                  subValue="within 5% of peak equity"
                  color="#06b6d4"
                />
              </div>
            </div>
          )}

          {/* Member-mode AI commentary — backend strips proprietary jargon */}
          {(() => {
            let apiPeriod = period;
            let extendedFlag = false;
            if (period === 'inception') apiPeriod = 'max';
            else if (period === 'extended') { apiPeriod = 'max'; extendedFlag = true; }
            const asOf = new URLSearchParams(window.location.search).get('as_of');
            const asOfQS = asOf ? `&as_of=${asOf}` : '';
            const extra = `tech=${LOCKED.tech}&momentum=${LOCKED.momentum}&def1=${LOCKED.def1}&def2=${LOCKED.def2}&cash=${LOCKED.cash}&extended=${extendedFlag}&member_view=true${asOfQS}`;
            return (
              <AiCommentary
                which="gt2"
                period={apiPeriod}
                extraQuery={extra}
                key={`nmv-${period}`}
              />
            );
          })()}

          <div className="bt-chart-section" data-testid="newmemberview-chart">
            <h2>Growth of $100,000: Strategy vs S&amp;P 500</h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={results.equity_curve}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontSize: 10 }}
                  interval={Math.floor(results.equity_curve.length / 12)}
                />
                <YAxis stroke="#94a3b8" tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#f8fafc',
                  }}
                  formatter={(value, name) => [`${value}%`, name]}
                  labelFormatter={(label) => `Date: ${label}`}
                  itemSorter={(item) =>
                    item.dataKey === 'portfolio_return' ? 0 : 1
                  }
                />
                <Legend />
                {results.extended_mode && results.splice_date && (
                  <ReferenceLine
                    x={results.splice_date}
                    stroke="#fbbf24"
                    strokeDasharray="4 4"
                    label={{ value: 'MTUM begins', position: 'top', fill: '#fbbf24', fontSize: 11 }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="portfolio_return"
                  name="The Serene System"
                  stroke="#06b6d4"
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="spy_return"
                  name="S&P 500 Buy & Hold"
                  stroke="#64748b"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="5 5"
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {results.extended_mode && (
              <div className="nmv-splice-note" data-testid="newmemberview-splice-note">
                {results.splice_note || 'XMMO used as proxy for MTUM from April 2013 back.'}
              </div>
            )}
          </div>

          {/* Full rotation history with sanitized reason column */}
          <div className="bt-trades-section" data-testid="newmemberview-trades">
            <div className="bt-trades-header">
              <h2>Position Changes ({results.total_trades_list_count} total)</h2>
              {results.trades.length > 15 && (
                <button
                  onClick={() => setShowAllTrades(!showAllTrades)}
                  className="bt-toggle-btn"
                  data-testid="newmemberview-toggle-trades"
                >
                  {showAllTrades ? 'Show Recent 15' : 'Show All'}
                </button>
              )}
            </div>
            <div className="bt-table-wrapper">
              <table className="bt-trades-table">
                <thead>
                  <tr>
                    <th>
                      <button
                        className="nmv-sort-btn"
                        onClick={() => setSortDesc(!sortDesc)}
                        title={sortDesc ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
                        data-testid="newmemberview-sort-toggle"
                      >
                        # {sortDesc ? '▼' : '▲'}
                      </button>
                    </th>
                    <th>Date</th>
                    <th>Action</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Exec Price</th>
                    <th>Portfolio Value</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sorted = [...results.trades].sort((a, b) =>
                      sortDesc
                        ? b.trade_number - a.trade_number
                        : a.trade_number - b.trade_number,
                    );
                    const visible = showAllTrades ? sorted : sorted.slice(0, 15);
                    return visible.map((trade, idx) => (
                    <tr key={idx}>
                      <td>{trade.trade_number}</td>
                      <td>{trade.date}</td>
                      <td>{trade.action}</td>
                      <td>
                        <span
                          className={`bt-position-tag ${positionTagClass(trade.from)}`}
                        >
                          {trade.from}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`bt-position-tag ${positionTagClass(trade.to)}`}
                        >
                          {trade.to}
                        </span>
                      </td>
                      <td>${trade.price?.toFixed(2)}</td>
                      <td>${trade.portfolio_value?.toLocaleString()}</td>
                      <td className="bt2-reason">{sanitizeReason(trade.reason)}</td>
                    </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mv-disclaimer" data-testid="newmemberview-disclaimer">
            Past performance is not a guarantee of future results. Performance shown
            reflects historical backtest results based on adjusted closing prices,
            including dividends. Live execution may vary due to slippage, market
            conditions, and broker execution timing.
          </div>
        </>
      )}
    </div>
  );
};

export default NewMemberView;
